import axios from 'axios';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '../config/constants';
import { toast } from 'react-toastify';
import { routes } from '../config/utility';
import { withInflightDedup } from './inflightDedup';


const forceLogout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem('roles');
    localStorage.removeItem('user');
    localStorage.removeItem('permission');
    window.location.href = routes.login;
};


// Create an Axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});


// Request Interceptor - Adding the token to each request
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY); // Retrieve decrypted token
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Let the browser set multipart boundary — manual Content-Type breaks file uploads
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            if (config.headers?.delete) {
                config.headers.delete('Content-Type');
            } else if (config.headers) {
                delete config.headers['Content-Type'];
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);


// Response Interceptor - Global response and error handling
api.interceptors.response.use(
    (response) => {
        // If the response contains a specific structure, simplify it
        if (response.data && response.data.result) {
            return response.data.result;
        }
        return response; // Return full response if structure is different
    },
    (error) => {
        // Handle common error responses globally here
        if (error.response) {
            const status = error.response.status;

            // Handle 401 Unauthorized
            if (status === 401) {
                toast.error('Session expired. Redirecting...', {
                    position: 'top-right',
                    autoClose: 3000,
                });
                forceLogout();
            }

            // Handle 422 Unprocessable Entity (validation errors)
            if (status === 422) {
                // Retrieve the error structure
                const errors = error.response.data.result?.data?.errors || {};

                // Prepare an array of error messages
                let errorMessages = [];

                if (Array.isArray(errors)) {
                    // If `errors` is an array (e.g., a single error array)
                    errorMessages = errors;
                } else if (typeof errors === 'object' && errors !== null) {
                    // If `errors` is an object, collect all error messages (Laravel-style errors)
                    errorMessages = Object.values(errors).flat(); // Flatten in case any value is an array
                }

                // Show a separate toast for each error message
                errorMessages.forEach((errorMessage) => {
                    toast.error(
                        <div className="flex items-center space-x-2">
                            <span className="font-semibold">{errorMessage}</span>
                        </div>,
                        {
                            position: 'top-right',
                            autoClose: 5000,
                            closeButton: ({ closeToast }) => (
                                <button
                                    onClick={closeToast}
                                    className="text-grey-900 hover:text-primary-300 transition duration-200 ease-in-out mb-2"
                                >
                                    ✕
                                </button>
                            ),
                        }
                    );
                });
            }



            // Handle other error statuses globally (500, 404, etc.)
            if (status >= 500) {
                const errors = error.response.data.result?.data?.errors || {};
                if (errors.error === "Unauthenticated.") {
                    localStorage.removeItem(AUTH_TOKEN_KEY); // Remove token
                    localStorage.removeItem('roles'); // Remove roles
                    localStorage.removeItem('user'); // Remove user
                }

                toast.error('Server error. Please try again later.', {
                    position: 'top-right',
                    autoClose: 5000,
                });
            }

            if (status === 404) {
                toast.error('Resource not found. Please check the URL.', {
                    position: 'top-right',
                    autoClose: 5000,
                });
            }

            // Optionally, log the error for debugging purposes
            console.error('API error:', error.response.data);
        } else if (error.request) {
            // Handle network error or no response from server
            toast.error('Network error. Please check your connection.', {
                position: 'top-right',
                autoClose: 5000,
            });
        } else {
            // Handle any other errors
            toast.error('An unexpected error occurred.', {
                position: 'top-right',
                autoClose: 5000,
            });
        }

        return Promise.reject(error); // Still reject the promise for additional handling
    }
);

// Deduplicate identical in-flight GETs (e.g. React StrictMode double effects)
api.get = withInflightDedup('get', api.get.bind(api));

// Parents Management API
export const parentsAPI = {
    // Get parents directory with filters
    getDirectory: (params) => api.get('/admin/parents', { params }),

    // Get parent statistics
    getStatistics: () => api.get('/admin/parents/statistics'),

    // Get family profile
    getProfile: (userId) => api.get(`/admin/parents/${userId}/profile`),

    // Get booking history
    getHistory: (userId) => api.get(`/admin/parents/${userId}/history`),

    // Get privacy & audit log
    getPrivacy: (userId) => api.get(`/admin/parents/${userId}/privacy`),

    // Perform admin action
    performAction: (userId, action, data = {}, config = {}) =>
        api.post(`/admin/parents/${userId}/action`, { action, data }, config),

    // Merge duplicate accounts
    mergeDuplicates: (primaryUserId, duplicateUserId) =>
        api.post('/admin/parents/merge', {
            primary_user_id: primaryUserId,
            duplicate_user_id: duplicateUserId
        })
};

// Children Management API
export const childrenAPI = {
    // Get children directory with filters
    getDirectory: (params) => api.get('/admin/children', { params }),

    // Get children statistics
    getStatistics: () => api.get('/admin/children/statistics'),

    // Get child profile
    getProfile: (childId) => api.get(`/admin/children/${childId}/profile`),

    // Get usage statistics
    getUsageStats: (childId) => api.get(`/admin/children/${childId}/usage`),

    // Get safety information
    getSafetyInfo: (childId) => api.get(`/admin/children/${childId}/safety`),

    // Get siblings
    getSiblings: (childId) => api.get(`/admin/children/${childId}/siblings`),

    // Perform admin action
    performAction: (childId, action, data = {}) =>
        api.post(`/admin/children/${childId}/action`, { action, data }),

    // Export children to CSV
    exportToCsv: (params) => api.get('/admin/children', { params, responseType: 'blob' })
};

// Assistant Admins Management API
export const assistantAdminsAPI = {
    // Get assistant admins directory with filters
    getDirectory: (params) => api.get('/assistant_admins', { params }),

    // Get dashboard statistics
    getDashboard: (params = {}) => api.get('/assistant_admins/dashboard', { params }),

    // Perform admin action
    performAction: (userId, action, data = {}, config = {}) =>
        api.post(`/assistant_admins/${userId}/action`, { action, data }, config),

    // Export assistant admins to CSV
    exportToCsv: (params) => api.get('/assistant_admins', { 
        params: { ...params, iscsv: true }, 
        responseType: 'blob' 
    })
};

export default api;
